<script setup lang="ts">
import { HISTORY_SCROLL_ICON_SVG } from "@/components/historyScrollIcon";
import { MAIN_NAV_ITEMS, type MainNavPage } from "@/navigation/mainNavigation";
import type { AuthUser } from "@/types/auth";

defineProps<{
  activePage: MainNavPage;
  currentUser?: AuthUser | null;
  archiveHome?: boolean;
  /** 地图星光灯光模式下，复用星云页导航效果（topbar.is-nebula） */
  forceNebulaStyle?: boolean;
  /** 开屏卷轴收起后，入口图标做一次脉冲提示 */
  scrollAttention?: boolean;
}>();

defineEmits<{
  brand: [];
  navigate: [page: MainNavPage];
  share: [];
  login: [];
  "open-scroll": [];
}>();

</script>

<template>
  <header
    class="topbar glass"
    :class="[`is-${activePage}`, { 'is-archive-home': archiveHome, 'is-nebula': forceNebulaStyle }]"
  >
    <div class="topbar-brand-group">
      <button class="brand brand-button" type="button" @click="$emit('brand')">
        <span class="brand-mark" aria-hidden="true">
          <img src="/images/logo.png" alt="江城星忆" class="brand-logo-img">
        </span>
        <span class="brand-copy">
          <strong>江城星忆</strong>
          <small>WUHAN MEMORY ATLAS</small>
        </span>
      </button>
      <button
        class="history-scroll-entry"
        :class="{ 'is-attention': scrollAttention }"
        type="button"
        title="江城记忆长卷"
        aria-label="打开江城记忆长卷"
        @click="$emit('open-scroll')"
        v-html="HISTORY_SCROLL_ICON_SVG"
      ></button>
    </div>

    <nav class="topbar-nav" aria-label="主导航">
      <button
        v-for="item in MAIN_NAV_ITEMS"
        :key="item.key"
        type="button"
        :class="{ 'is-active': activePage === item.key }"
        :aria-current="activePage === item.key ? 'page' : undefined"
        :aria-label="`${item.label}：${item.description}`"
        @click="$emit('navigate', item.key)"
      >
        <span class="primary-nav-label">{{ item.label }}</span>
        <span class="primary-nav-tooltip" aria-hidden="true">{{ item.description }}</span>
      </button>
    </nav>

    <div class="topbar-actions">
      <button class="topbar-share" type="button" aria-label="分享" title="分享" @click="$emit('share')">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3v11" />
          <path d="m7 8 5-5 5 5" />
          <path d="M5 12v7h14v-7" />
        </svg>
      </button>
      <button
        class="topbar-login"
        type="button"
        :aria-label="currentUser ? '用户中心' : '登录'"
        :title="currentUser ? currentUser.displayName : '登录'"
        @click="$emit('login')"
      >
        <img
          v-if="currentUser?.avatarUrl"
          :src="currentUser.avatarUrl"
          :alt="currentUser.displayName"
        />
        <span v-else-if="currentUser" class="topbar-avatar-initial">
          {{ currentUser.displayName.trim().slice(0, 1).toUpperCase() }}
        </span>
        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M4.5 20c1.7-4 4.2-6 7.5-6s5.8 2 7.5 6" />
        </svg>
      </button>
    </div>
  </header>
</template>
